import * as React from 'react';
import moment from 'moment';
import Paths from '../models/enums/paths';
import { AppContext } from '../shared/app-context';
import { useHistory } from 'react-router-dom';
import { DashboardReport } from '../components/dashboard/dashboard-view';
import LastUpdate from '../models/enums/lastUpdate';
import HelpDocument from '../models/helpDocument';
import useRestService from '../shared/rest-service';

/**
 * Custom hook which retrieves report data and report help documents
 *
 * @param clientId current client id
 * @param reportId id of the current report (from useParams)
 */
const useReports = (clientId: number, reportId: string) => {
  const appContext = React.useContext(AppContext);
  const { getAuth } = useRestService();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [report, setReport] = React.useState<DashboardReport>(null);
  const [documents, setDocuments] = React.useState<HelpDocument[]>([]);
  const [lastUpdateDates, setLastUpdateDates] = React.useState<LastUpdate[]>([]);
  const history = useHistory();

  /** on load, get help docs, report data and latest update dates */
  React.useEffect(() => {
    if (appContext.state.clientChanging) {
      history.replace(Paths.Home, { redirect: true });
    }

    let isMounted = true;

    Promise.all([fetchHelpDocuments(), fetchReport()])
      .then(([documents, report]) => {
        if (isMounted) {
          if (!report || report.reportId === 0 || report.reportContentViewModels === null) {
            history.replace('/', { error: 'Report not found for the current client' });
          } else {
            setDocuments(documents);
            setReport(report);
            /** fetch lastUpdateDates once the report name is available */
            fetchLastUpdates(report.name)
              .then((lastUpdateDates: LastUpdate[]) => {
                if (isMounted) {
                  setLastUpdateDates(lastUpdateDates);
                  setLoading(false);
                }
              })
              .catch((e) => {
                setLoading(false);
                console.log(e);
              });
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
    return function cleanup() {
      isMounted = false;
    };
  }, []);

  const fetchReport = () => {
    return getAuth(`api/${clientId}/reports/${reportId}`);
  };

  const fetchLastUpdates = (dashboardName: string) => {
    return getAuth(`api/${clientId}/reports/${reportId}/last-update-dates?dashboard=${dashboardName}`).then(
      (dates: LastUpdate[] | undefined) => {
        if (dates && dates.length > 0) {
          let sorted = [...dates].sort((a, b) => (a.LastUpdated >= b.LastUpdated ? 1 : -1));
          return sorted.map((date) => {
            return {
              ...date,
              LastUpdated: moment(date.LastUpdated).format('L, h:mm:ss a').replace(/,/g, '')
            };
          });
        }
        return [];
      }
    );
  };

  const fetchHelpDocuments = () => {
    return getAuth(`api/${clientId}/reports/${reportId}/help`);
  };

  const refreshLastUpdate = () => {
    if (report) {
      fetchLastUpdates(report.name)
        .then((lastUpdateDates: LastUpdate[]) => setLastUpdateDates(lastUpdateDates))
        .catch((e) => {
          console.log(e);
        });
    }
  };

  return {
    loading,
    report,
    documents,
    lastUpdateDates,
    refreshLastUpdate
  };
};

export default useReports;
